import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Helmet } from 'react-helmet-async';
import { useAuth } from './contexts/AuthContext';
import { ConfirmProvider } from './contexts/ConfirmContext';
import { installClipboardCleaner } from './utils/clipboardCleaner';

// Public pages — loaded eagerly so the unauthenticated shell stays minimal
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import OAuthCallbackPage from './pages/OAuthCallbackPage';
import IntegrationsCallbackPage from './pages/IntegrationsCallbackPage';
import VerifyEmailPage from './pages/VerifyEmailPage';

// Authenticated pages — lazy-loaded so they are excluded from the public bundle
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const JobsPage = React.lazy(() => import('./pages/JobsPage'));
const JobDetailPage = React.lazy(() => import('./pages/JobDetailPage'));
const JobCreatePage = React.lazy(() => import('./pages/JobCreatePage'));
const JobEditPage = React.lazy(() => import('./pages/JobEditPage'));
const CandidatesPage = React.lazy(() => import('./pages/CandidatesPage'));
const CandidateDetailPage = React.lazy(() => import('./pages/CandidateDetailPage'));
const CandidateCreatePage = React.lazy(() => import('./pages/CandidateCreatePage'));
const CandidateEditPage = React.lazy(() => import('./pages/CandidateEditPage'));
const GenerateReportPage = React.lazy(() => import('./pages/GenerateReportPage'));
const ReportsPage = React.lazy(() => import('./pages/ReportsPage'));
const InterviewsPage = React.lazy(() => import('./pages/InterviewsPage'));
const InterviewCreatePage = React.lazy(() => import('./pages/InterviewCreatePage'));
const InterviewEditPage = React.lazy(() => import('./pages/InterviewEditPage'));
const UserSettingsPage = React.lazy(() => import('./pages/UserSettingsPage'));
const SectorResearchPage = React.lazy(() => import('./pages/SectorResearchPage'));
const GuidedResearchPage = React.lazy(() => import('./pages/GuidedResearchPage'));
const CandidateSearchPage = React.lazy(() => import('./pages/CandidateSearchPage'));
const CandidateSearchResultsPage = React.lazy(() => import('./pages/CandidateSearchResultsPage'));
const QuickCandidateSearchPage = React.lazy(() => import('./pages/QuickCandidateSearchPage'));
const QuickCandidateSearchResultsPage = React.lazy(() => import('./pages/QuickCandidateSearchResultsPage'));
const QuickSearchHistoryPage = React.lazy(() => import('./pages/QuickSearchHistoryPage'));
const DeepSearchPage = React.lazy(() => import('./pages/DeepSearchPage'));
const SectorIntelPage = React.lazy(() => import('./pages/SectorIntelPage'));
const SupportTicketPage = React.lazy(() => import('./pages/SupportTicketPage'));
const HelpPage = React.lazy(() => import('./pages/HelpPage'));
const IntegrationsPage = React.lazy(() => import('./pages/IntegrationsPage'));
const PricingPage = React.lazy(() => import('./pages/PricingPage'));
const MainLayout = React.lazy(() => import('./components/Layout/MainLayout'));

// Admin pages — separate lazy chunk
const AdminUserManagementPage = React.lazy(() => import('./pages/AdminUserManagementPage'));
const AdminAuditLogsPage = React.lazy(() => import('./pages/AdminAuditLogsPage'));
const AdminPromptTemplatesPage = React.lazy(() => import('./pages/AdminPromptTemplatesPage'));
const AdminReportTemplatesPage = React.lazy(() => import('./pages/AdminReportTemplatesPage'));
const AdminAnnouncementsPage = React.lazy(() => import('./pages/AdminAnnouncementsPage'));
const AdminSubscriptionPlansPage = React.lazy(() => import('./pages/AdminSubscriptionPlansPage'));
const AdminSystemSettingsPage = React.lazy(() => import('./pages/AdminSystemSettingsPage'));
const AdminAiModelsPage = React.lazy(() => import('./pages/AdminAiModelsPage'));

// Minimal fallback shown while lazy chunks load
const PageLoader: React.FC = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#02140F' }} />
);

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireAdmin = false }) => {
  const { user } = useAuth();
  const location = useLocation();
  const isAuthenticated = user !== null;
  const isAdmin = user?.role === 'admin';

  if (!isAuthenticated) {
    const returnTo = location.pathname + location.search;
    return <Navigate to={`/auth/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const { user } = useAuth();
  const isAuthenticated = !!user;
  const navigate = useNavigate();

  // Handle 401 responses that originate from the API layer (api.ts dispatches this
  // event instead of doing a hard window.location.href redirect, so the SPA history
  // stack is preserved and a returnTo URL can be passed through cleanly).
  useEffect(() => {
    const handleSessionExpired = (event: Event) => {
      const detail = (event as CustomEvent<{ returnTo?: string }>).detail;
      const returnTo = detail?.returnTo || '/dashboard';
      navigate(`/auth/login?returnTo=${encodeURIComponent(returnTo)}`, { replace: true });
    };
    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, [navigate]);

  // Strip dark background colours from clipboard HTML on every copy operation
  // so that pasting into Word or Google Docs doesn't produce text with a black
  // background.
  useEffect(() => installClipboardCleaner(), []);

  return (
    <ConfirmProvider>
      <Helmet titleTemplate="%s | Trova" defaultTitle="Trova" />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Navigate to={isAuthenticated ? "/dashboard" : "/auth/login"} replace />} />
          <Route path="/auth/login" element={<LoginPage />} />
          <Route path="/auth/register" element={<RegisterPage />} />
          <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/oauth-callback" element={<OAuthCallbackPage />} />
          <Route path="/integrations/callback" element={<IntegrationsCallbackPage />} />

          {/* Protected Routes */}
          <Route path="/" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            <Route path="dashboard" element={<DashboardPage />} />

            {/* Jobs Routes */}
            <Route path="jobs" element={<JobsPage />} />
            <Route path="jobs/new" element={<JobCreatePage />} />
            <Route path="jobs/:jobId" element={<JobDetailPage />} />
            <Route path="jobs/:jobId/edit" element={<JobEditPage />} />
            <Route path="jobs/:jobId/sector-research" element={<SectorResearchPage />} />

            {/* Candidates Routes */}
            <Route path="candidates" element={<CandidatesPage />} />
            <Route path="candidates/new" element={<CandidateCreatePage />} />
            <Route path="candidates/:candidateId" element={<CandidateDetailPage />} />
            <Route path="candidates/:candidateId/edit" element={<CandidateEditPage />} />

            {/* Reports Routes */}
            <Route path="generate-report" element={<GenerateReportPage />} />
            <Route path="reports" element={<ReportsPage />} />

            {/* Integrations Route */}
            <Route path="integrations" element={<IntegrationsPage />} />

            {/* Support Ticket Route */}
            <Route path="support" element={<SupportTicketPage />} />
            <Route path="help" element={<HelpPage />} />

            {/* Interview Routes */}
            <Route path="interviews" element={<InterviewsPage />} />
            <Route path="interviews/new" element={<InterviewCreatePage />} />
            <Route path="interviews/:interviewId/edit" element={<InterviewEditPage />} />

            {/* Guided Research Route */}
            <Route path="guided-research" element={<GuidedResearchPage />} />

            {/* Candidate Search Routes */}
            <Route path="candidate-search" element={<CandidateSearchPage />} />
            <Route path="candidate-search/results" element={<CandidateSearchResultsPage />} />

            {/* Quick Candidate Search Routes */}
            <Route path="quick-search" element={<QuickCandidateSearchPage />} />
            <Route path="quick-search/results" element={<QuickCandidateSearchResultsPage />} />
            <Route path="quick-search/history" element={<QuickSearchHistoryPage />} />
            <Route path="quick-search/history/:historyId" element={<QuickCandidateSearchResultsPage />} />

            {/* Deep Search — standalone route removed; embedded via assignment Research tab */}
            <Route path="deep-search" element={<Navigate to="/jobs" replace />} />
            <Route path="deep-search/:sessionId" element={<Navigate to="/jobs" replace />} />

            {/* Trova Compass Routes - same key prevents unmount/remount on navigate */}
            <Route path="trova-compass" element={<SectorIntelPage key="trova-compass" />} />
            <Route path="trova-compass/:sessionId" element={<SectorIntelPage key="trova-compass" />} />

            {/* Pricing Route */}
            <Route path="pricing" element={<PricingPage />} />

            {/* Settings Route */}
            <Route path="settings" element={<UserSettingsPage />} />
          </Route>

          {/* Admin Routes */}
          <Route path="/admin" element={<ProtectedRoute requireAdmin={true}><MainLayout /></ProtectedRoute>}>
            <Route index element={<Navigate to="/admin/users" replace />} />
            <Route path="users" element={<AdminUserManagementPage />} />
            <Route path="audit-logs" element={<AdminAuditLogsPage />} />
            <Route path="prompt-templates" element={<AdminPromptTemplatesPage />} />
            <Route path="report-templates" element={<AdminReportTemplatesPage />} />
            <Route path="announcements" element={<AdminAnnouncementsPage />} />
            <Route path="subscription-plans" element={<AdminSubscriptionPlansPage />} />
            <Route path="system-settings" element={<AdminSystemSettingsPage />} />
            <Route path="ai-models" element={<AdminAiModelsPage />} />
          </Route>

          {/* Fallback Route - Redirect to Landing Page */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </ConfirmProvider>
  );
};

export default App;
